import { Link } from 'react-router-dom';
import { StatusBadge, type StatusKind } from '../../../shared/dashboard/StatusBadge';
import { UnavailableState } from '../../../shared/dashboard/UnavailableState';
import { formatDateOnly } from '../../../shared/dashboard/dashboardFormat';
import type { AlertSeverity, ExecutiveAlert, Obligation, PayrollStatusCount, TaxAlert } from '../../dashboard-v2/types';
import { formatCurrency } from '../../dashboard-v2/types';

const SEVERITY: Record<AlertSeverity, { kind: StatusKind; label: string }> = {
  critical: { kind: 'critical', label: 'Critical' },
  attention: { kind: 'attention', label: 'Needs attention' },
  information: { kind: 'info', label: 'For your information' },
};

/**
 * Alerts and To-Dos, most serious first. Every alert is derived from real application state on the server (an unpaid tax amount past its due date, payroll
 * waiting for an approver); nothing here is decoration. Each shows its severity as an icon and a word, never colour alone, and links to the screen where it is
 * resolved.
 */
export function ExecutiveAlertsPanel({ alerts, unavailable }: { alerts: ExecutiveAlert[]; unavailable: boolean }) {
  return (
    <section className="ed-section-card" data-testid="executive-alerts-panel">
      <div className="ed-section-header ed-alerts-header">
        <div className="ed-section-header-left">
          <span className="ed-section-icon ed-alerts-icon" aria-hidden="true">🔔</span>
          <h2 className="ed-section-title ed-alerts-title">Alerts and To-Dos</h2>
        </div>
        <span className="ed-sort-control">Most serious first</span>
      </div>

      {unavailable && alerts.length === 0 && (
        <div className="ed-section-error">
          <UnavailableState reason="Some alerts could not be checked because a service could not be reached. Try again shortly." />
        </div>
      )}
      {!unavailable && alerts.length === 0 && <div className="ed-empty-state">No alerts or to-dos right now.</div>}

      {alerts.length > 0 && (
        <div className="ed-alerts-list">
          {alerts.map((alert) => (
            <Link key={`${alert.title}-${alert.detail}`} to={alert.actionPath} className="ed-alert-row" aria-label={`${SEVERITY[alert.severity].label}: ${alert.title}. ${alert.detail}`}>
              <div>
                <p className="ed-alert-title">
                  <StatusBadge kind={SEVERITY[alert.severity].kind}>{SEVERITY[alert.severity].label}</StatusBadge> {alert.title}
                </p>
                <p className="ed-alert-desc">{alert.detail}</p>
              </div>
              <span className="ed-alert-chevron" aria-hidden="true">›</span>
            </Link>
          ))}
        </div>
      )}
      {unavailable && alerts.length > 0 && (
        <p className="ed-donut-empty ed-donut-empty--footnote">Some tax alerts could not be checked because the tax service could not be reached.</p>
      )}
    </section>
  );
}

/** Payroll runs by status in Payroll Review. Later states (funding, processing, failed, reversed) are not shown because the dashboard cannot read payment records yet. */
export function PayrollQueuePanel({ statuses }: { statuses: PayrollStatusCount[] | null }) {
  return (
    <section className="ed-card ed-queue-card" data-testid="payroll-queue-card" aria-labelledby="ed-queue-title">
      <h3 id="ed-queue-title" className="ed-chart-title">Payroll Status</h3>
      {statuses === null ? (
        <UnavailableState reason="The payroll service could not be reached, so payroll counts cannot be shown." />
      ) : (
        <ul className="ed-queue-list">
          {statuses.map((s) => (
            <li key={s.status}>
              {s.actionPath ? (
                <Link to={s.actionPath} className="ed-queue-row" aria-label={`${s.label}: ${s.count}. View in Payroll Review.`}>
                  <span>{s.label}</span>
                  <strong>{s.count}</strong>
                </Link>
              ) : (
                <div className="ed-queue-row"><span>{s.label}</span><strong>{s.count}</strong></div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * What is coming up. Two different kinds of date are kept apart on purpose: a RETURN FILING date (from the federal calendar, already moved for weekends and
 * holidays) and an unpaid tax AMOUNT with the date its return is due. Neither is a tax deposit deadline, which depends on the company's deposit schedule and
 * is listed as unavailable below.
 */
export function ObligationsPanel({
  obligations, unpaid, obligationsUnavailable, taxesUnavailable,
}: {
  obligations: Obligation[] | null;
  unpaid: TaxAlert[];
  obligationsUnavailable: boolean;
  taxesUnavailable: boolean;
}) {
  return (
    <section className="ed-card ed-obligations-card" data-testid="obligations-card" aria-labelledby="ed-obligations-title">
      <h3 id="ed-obligations-title" className="ed-chart-title">Upcoming Obligations</h3>

      <h4 className="ed-subheading">Return filing dates</h4>
      {obligationsUnavailable || obligations === null ? (
        <UnavailableState reason="The federal tax calendar could not be reached, so filing dates cannot be shown." />
      ) : obligations.length === 0 ? (
        <p className="ed-donut-empty ed-donut-empty--footnote">No upcoming return filing dates.</p>
      ) : (
        <ul className="ed-obligation-list">
          {obligations.map((o) => (
            <li key={`${o.title}-${o.date}`}>
              <Link to={o.actionPath} className="ed-obligation-row">
                <span className="ed-obligation-row__date">{formatDateOnly(o.date)}</span>
                <span>
                  <strong>{o.title}</strong>
                  <span className="ed-obligation-row__note">{o.note}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <h4 className="ed-subheading">Unpaid tax amounts</h4>
      {taxesUnavailable ? (
        <UnavailableState reason="The tax service could not be reached, so unpaid amounts cannot be shown." />
      ) : unpaid.length === 0 ? (
        <p className="ed-donut-empty ed-donut-empty--footnote">No unpaid tax amounts.</p>
      ) : (
        <ul className="ed-obligation-list">
          {unpaid.map((u) => (
            <li key={`${u.formLabel}-${u.dueDate}-${u.amount}`}>
              <Link to="/payroll-taxes" className="ed-obligation-row">
                <span className="ed-obligation-row__date">{formatDateOnly(u.dueDate)}</span>
                <span>
                  <strong>{u.formLabel}: {formatCurrency(u.amount)}</strong>
                  {u.isOverdue && <> <StatusBadge kind="critical">Overdue</StatusBadge></>}
                  <span className="ed-obligation-row__note">Return due date. Not a deposit deadline.</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
